/**
 * AgencyPage — CRUD list for linked agency client workspaces.
 * Header + link-client form + table of links with delete action.
 */
import { useState, type FormEvent } from "react";
import {
	useAgencyList,
	useAgencyCreate,
	useAgencyDelete,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";

export function AgencyPage() {
	const listQuery = useAgencyList();
	const createMut = useAgencyCreate();
	const deleteMut = useAgencyDelete();

	const [name, setName] = useState("");

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;
		createMut.mutate(
			{ name: name.trim() },
			{
				onSuccess: () => setName(""),
			},
		);
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="agency-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					agency
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-3xl font-semibold text-transparent">
					Agency
				</h1>
				<p className="max-w-2xl text-sm text-zinc-500">
					Link client workspaces to this agency and manage active delegations.
				</p>
			</header>

			<section className={CARD} aria-label="Link client workspace">
				<h2 className="text-base font-semibold text-zinc-900">
					Link a client workspace
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="agency-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="Client workspace slug"
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						disabled={createMut.isPending}
						data-testid="agency-name-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending || !name.trim()}
						data-testid="agency-submit"
					>
						{createMut.isPending ? "Saving..." : "Link"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Linked client workspaces">
				<h2 className="text-base font-semibold text-zinc-900">
					Linked client workspaces
				</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="agency-loading"
					>
						Loading...
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="agency-empty"
					>
						No client workspaces linked yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="agency-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between gap-3 py-2 text-sm"
								data-testid={`agency-row-${row.id}`}
							>
								<div className="flex min-w-0 flex-col gap-0.5">
									<span className="truncate font-medium text-zinc-900">
										{row.client_name || row.client_slug || row.client_tenant_id}
									</span>
									<span className="font-mono text-[11px] text-zinc-500">
										{row.client_tenant_id}
										{row.role ? ` • ${row.role}` : ""}
										{row.status ? ` • ${row.status}` : ""}
									</span>
								</div>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(row.id)}
									disabled={deleteMut.isPending}
									data-testid={`agency-delete-${row.id}`}
								>
									Unlink
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AgencyPage;
